import type { ProcessedHomeResult } from '@/domains/home/home.entity';
import type { Center, Day } from '@/domains/shared';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { HomeStoreEntity, HomeStoreRepository } from '../models';

/**
 * Valores por defecto del store
 */
const defaultHomeState: HomeStoreEntity = {
  metricsBySede: [],
  metricsSummary: {
    total_estudiantes: 0,
    total_riesgo: 0,
  },
  activeFilter: '',
  searchQuery: '',
  isLoading: false,
  activeJornada: null,
  activeSede: null,
};

/**
 * Store de Zustand para la vista Home/Dashboard
 * Gestiona métricas por sede, búsqueda, filtros y selección activa de jornada/sede
 * Persiste: activeFilter, searchQuery, activeJornada, activeSede en localStorage
 */
export const useHomeStore = create<HomeStoreRepository>()(
  persist(
    (set, get) => ({
      ...defaultHomeState,

      /**
       * Carga datos procesados del home desde la API
       * Actualiza métricas por sede y resumen total
       *
       * @param processedData - Datos procesados con métricas por sede y summary
       */
      loadHome: (processedData: ProcessedHomeResult) => {
        const { metricsBySede } = get();
        if (metricsBySede === processedData.metricsBySede) return;

        set({
          metricsBySede: processedData.metricsBySede,
          metricsSummary: processedData.metricsSummary,
          isLoading: false,
        });
      },

      /**
       * Establece el filtro activo de indicadores de riesgo
       *
       * @param filter - Identificador del filtro (ej: 'notas', 'asistencia')
       */
      setFilter: (filter: string) => {
        set({ activeFilter: filter });
      },

      /**
       * Actualiza el término de búsqueda
       *
       * @param query - Término de búsqueda
       */
      setSearchQuery: (query: string) => {
        set({ searchQuery: query });
      },

      /**
       * Establece la jornada activa para drill-down
       *
       * @param jornada - Jornada seleccionada ('Diurna' | 'Vespertina') o null
       */
      setActiveJornada: (jornada: Day | null) => {
        set({ activeJornada: jornada });
      },

      /**
       * Establece la sede activa para drill-down
       *
       * @param sede - Centro/sede seleccionada o null
       */
      setActiveSede: (sede: Center | null) => {
        set({ activeSede: sede });
      },

      /**
       * Controla el estado de carga de datos
       *
       * @param loading - true cuando se están cargando datos
       */
      setLoading: (loading: boolean) => set({ isLoading: loading }),

      /**
       * Resetea el store a su estado inicial
       * Limpia todos los datos y filtros
       */
      clearHome: () => set({ ...defaultHomeState }),
    }),
    {
      name: '@app/home',
      partialize: (state: HomeStoreRepository) => ({
        activeFilter: state.activeFilter,
        searchQuery: state.searchQuery,
        activeJornada: state.activeJornada,
        activeSede: state.activeSede,
      }),
    },
  ),
);
